'use client';

import type { LocalUserChoices } from '@livekit/components-core';
import { loadUserChoices, log } from '@livekit/components-core';
import { roomOptionsStringifyReplacer } from '@/lib/utils';
import { useMediaDevices, usePersistentUserChoices } from '@livekit/components-react/hooks';
import { useWarnAboutMissingStyles } from '@/hooks/useWarnAboutMissingStyles';
import { useSettingsPersistence } from '@/hooks/useSettingsPersistence';
import { useCustomBackgroundImage } from '@/hooks/useCustomBackgroundImage';
import { applyBackgroundToVideoTrack, BACKGROUND_DEBOUNCE_MS } from '@/lib/background-processor';
import { unlockBrowserAudioPlayback } from '@/lib/audio-playback-unlock';
import { isWeakClientDevice } from '@/lib/client-utils';
import { PrejoinVideoPreview } from '@/lib/PrejoinVideoPreview';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { useOptionalVideoQuality } from '@/lib/VideoQualityContext';
import { getVideoQualityRoomCapture } from '@/lib/video-quality-profile';
import { DeviceSettingsModal } from '@/components/DeviceSettingsModal';
import { MobileSettingsModal } from '@/components/MobileSettingsModal';
import { MobileInviteModal } from '@/components/MobileInviteModal';
import { PreJoinSettingsMenu } from '@/components/PreJoinSettingsMenu';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VolumeX } from 'lucide-react';
import { CrownIcon } from '@/components/ui/crown-icon';
import { CustomChevronDown } from '@/components/ui/custom-chevron-down';

import type {
  CreateLocalTracksOptions,
  LocalAudioTrack,
  LocalTrack,
  LocalVideoTrack,
  TrackProcessor,
  VideoCaptureOptions,
  VideoResolution,
} from 'livekit-client';
import {
  createLocalAudioTrack,
  createLocalTracks,
  createLocalVideoTrack,
  facingModeFromLocalTrack,
  Mutex,
  Track,
  VideoPresets,
} from 'livekit-client';
import {
  isInvalidMediaConstraintError,
  toSafeDeviceIdConstraint,
} from '@/lib/media-capture-constraints';
import * as React from 'react';
import Link from 'next/link';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  LogIn,
  Video,
  Mic,
  MicOff,
  VideoOff,
  ArrowLeft,
  Settings,
  Image as ImageIcon,
  UserPlus,
  ChevronDown,
  ChevronUp,
  Square,
  Copy,
  Mail,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/** @public */
export interface PreJoinProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit' | 'onError'> {
  /** This function is called with the `LocalUserChoices` if validation is passed. */
  onSubmit?: (values: LocalUserChoices) => void;
  /**
   * Provide your custom validation function. Only if validation is successful the user choices are past to the onSubmit callback.
   */
  onValidate?: (values: LocalUserChoices) => boolean;
  onError?: (error: Error) => void;
  /** Prefill the input form with initial values. */
  defaults?: Partial<LocalUserChoices>;
  /** Display a debug window for your convenience. */
  debug?: boolean;
  joinLabel?: string;
  micLabel?: string;
  camLabel?: string;
  userLabel?: string;
  /** Name of the room to join */
  roomName?: string;
  /**
   * If true, user choices are persisted across sessions.
   * @defaultValue true
   * @alpha
   */
  persistUserChoices?: boolean;
  videoProcessor?: TrackProcessor<Track.Kind.Video>;
  /** If true, user will join as host */
  isHost?: boolean;
  /** @deprecated use video quality profile via VideoQualityProvider */
  hq?: boolean;
}

/** @public */
export function usePreviewTracks(
  options: CreateLocalTracksOptions,
  onError?: (err: Error) => void,
) {
  const [tracks, setTracks] = React.useState<LocalTrack[]>();

  const trackLock = React.useMemo(() => new Mutex(), []);

  React.useEffect(() => {
    let needsCleanup = false;
    let localTracks: Array<LocalTrack> = [];
    trackLock.lock().then(async (unlock) => {
      try {
        if (options.audio || options.video) {
          localTracks = await createLocalTracks(options);

          if (needsCleanup) {
            localTracks.forEach((tr) => tr.stop());
          } else {
            setTracks(localTracks);
          }
        }
      } catch (e: unknown) {
        if (onError && e instanceof Error) {
          onError(e);
        } else {
          log.error(e);
        }
      } finally {
        unlock();
      }
    });

    return () => {
      needsCleanup = true;
      localTracks.forEach((track) => {
        track.stop();
      });
    };
  }, [JSON.stringify(options, roomOptionsStringifyReplacer), onError, trackLock]);

  return tracks;
}

/** Hook separado para track de áudio */
export function usePreviewAudioTrack(
  enabled: boolean,
  deviceId?: string,
  onError?: (err: Error) => void,
) {
  const [audioTrack, setAudioTrack] = React.useState<LocalAudioTrack>();
  const trackLock = React.useMemo(() => new Mutex(), []);
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  React.useEffect(() => {
    let needsCleanup = false;
    let localTrack: LocalAudioTrack | undefined;

    trackLock.lock().then(async (unlock) => {
      try {
        if (enabled) {
          const attempts: Array<{ deviceId?: ConstrainDOMString }> = [
            { deviceId: toSafeDeviceIdConstraint(deviceId) },
            { deviceId: { ideal: 'default' } },
            {},
          ];
          let lastError: unknown;
          for (const opts of attempts) {
            try {
              localTrack = await createLocalAudioTrack(
                opts.deviceId !== undefined ? { deviceId: opts.deviceId } : {},
              );
              lastError = undefined;
              break;
            } catch (e) {
              lastError = e;
              if (!isInvalidMediaConstraintError(e) && !(e instanceof DOMException)) {
                break;
              }
            }
          }
          if (lastError) {
            throw lastError;
          }

          if (needsCleanup) {
            localTrack?.stop();
            return;
          }
          setAudioTrack(localTrack);
        } else {
          setAudioTrack(undefined);
        }
      } catch (e: unknown) {
        if (onErrorRef.current && e instanceof Error) {
          onErrorRef.current(e);
        } else {
          log.error(e);
        }
      } finally {
        unlock();
      }
    });

    return () => {
      needsCleanup = true;
      localTrack?.stop();
    };
  }, [enabled, deviceId, trackLock]);

  return audioTrack;
}

/** Hook separado para track de vídeo */
export function usePreviewVideoTrack(
  enabled: boolean,
  deviceId?: string,
  onError?: (err: Error) => void,
  captureResolution?: VideoResolution,
) {
  const [videoTrack, setVideoTrack] = React.useState<LocalVideoTrack>();
  const trackLock = React.useMemo(() => new Mutex(), []);
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  const captureWidth = captureResolution?.width;
  const captureHeight = captureResolution?.height;

  React.useEffect(() => {
    if (!enabled) {
      setVideoTrack(undefined);
      return;
    }

    let cancelled = false;
    let createdTrack: LocalVideoTrack | undefined;

    const run = async () => {
      const unlock = await trackLock.lock();
      try {
        if (cancelled) {
          return;
        }

        const safeDeviceId = toSafeDeviceIdConstraint(deviceId);
        const withResolution =
          captureWidth && captureHeight
            ? { resolution: { width: captureWidth, height: captureHeight } }
            : {};

        const attempts: VideoCaptureOptions[] = [
          {
            ...(safeDeviceId !== undefined ? { deviceId: safeDeviceId } : {}),
            ...withResolution,
          },
          {
            ...(safeDeviceId !== undefined ? { deviceId: safeDeviceId } : {}),
            resolution: VideoPresets.h360.resolution,
          },
          {
            ...(safeDeviceId !== undefined ? { deviceId: safeDeviceId } : {}),
          },
          { deviceId: { ideal: 'default' } },
          {},
        ];

        let lastError: unknown;
        for (const opts of attempts) {
          try {
            createdTrack = await createLocalVideoTrack(opts);
            lastError = undefined;
            break;
          } catch (e) {
            lastError = e;
            if (
              e instanceof DOMException &&
              (e.name === 'NotAllowedError' || e.name === 'SecurityError')
            ) {
              break;
            }
          }
        }
        if (lastError || !createdTrack) {
          throw lastError ?? new Error('Não foi possível iniciar a câmera');
        }

        if (cancelled) {
          createdTrack.stop();
          return;
        }

        setVideoTrack((prev) => {
          if (prev && prev !== createdTrack) {
            prev.stop();
          }
          return createdTrack;
        });
      } catch (e: unknown) {
        if (onErrorRef.current && e instanceof Error) {
          onErrorRef.current(e);
        } else {
          log.error(e);
        }
      } finally {
        unlock();
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (createdTrack) {
        createdTrack.stop();
        setVideoTrack((prev) => (prev === createdTrack ? undefined : prev));
      }
    };
  }, [enabled, deviceId, trackLock, captureWidth, captureHeight]);

  return videoTrack;
}

/**
 * @public
 * @deprecated use `usePreviewTracks` instead
 */
export function usePreviewDevice<T extends LocalVideoTrack | LocalAudioTrack>(
  enabled: boolean,
  deviceId: string,
  kind: 'videoinput' | 'audioinput',
) {
  const [deviceError, setDeviceError] = React.useState<Error | null>(null);
  const [isCreatingTrack, setIsCreatingTrack] = React.useState<boolean>(false);

  const devices = useMediaDevices({ kind });
  const [selectedDevice, setSelectedDevice] = React.useState<MediaDeviceInfo | undefined>(
    undefined,
  );

  const [localTrack, setLocalTrack] = React.useState<T>();
  const [localDeviceId, setLocalDeviceId] = React.useState<string>(deviceId);

  React.useEffect(() => {
    setLocalDeviceId(deviceId);
  }, [deviceId]);

  const createTrack = async (deviceId: string, kind: 'videoinput' | 'audioinput') => {
    try {
      const track =
        kind === 'videoinput'
          ? await createLocalVideoTrack({
              deviceId,
              resolution: VideoPresets.h720.resolution,
            })
          : await createLocalAudioTrack({ deviceId });

      const newDeviceId = await track.getDeviceId(false);
      if (newDeviceId && deviceId !== newDeviceId) {
        prevDeviceId.current = newDeviceId;
        setLocalDeviceId(newDeviceId);
      }
      setLocalTrack(track as T);
    } catch (e) {
      if (e instanceof Error) {
        setDeviceError(e);
      }
    }
  };

  const switchDevice = async (track: LocalVideoTrack | LocalAudioTrack, id: string) => {
    await track.setDeviceId(id);
    prevDeviceId.current = id;
  };

  const prevDeviceId = React.useRef(localDeviceId);

  React.useEffect(() => {
    if (enabled && !localTrack && !deviceError && !isCreatingTrack) {
      log.debug('creating track', kind);
      setIsCreatingTrack(true);
      createTrack(localDeviceId, kind).finally(() => {
        setIsCreatingTrack(false);
      });
    }
  }, [enabled, localTrack, deviceError, isCreatingTrack]);

  // switch camera device
  React.useEffect(() => {
    if (!localTrack) {
      return;
    }
    if (!enabled) {
      log.debug(`muting ${kind} track`);
      localTrack.mute().then(() => log.debug(localTrack.mediaStreamTrack));
    } else if (selectedDevice?.deviceId && prevDeviceId.current !== selectedDevice?.deviceId) {
      log.debug(`switching ${kind} device from`, prevDeviceId.current, selectedDevice.deviceId);
      switchDevice(localTrack, selectedDevice.deviceId);
    } else {
      log.debug(`unmuting local ${kind} track`);
      localTrack.unmute();
    }
  }, [localTrack, selectedDevice, enabled, kind]);

  React.useEffect(() => {
    return () => {
      if (localTrack) {
        log.debug(`stopping local ${kind} track`);
        localTrack.stop();
        localTrack.mute();
      }
    };
  }, []);

  React.useEffect(() => {
    setSelectedDevice(devices?.find((dev) => dev.deviceId === localDeviceId));
  }, [localDeviceId, devices]);

  return {
    selectedDevice,
    localTrack,
    deviceError,
  };
}

/**
 * The `PreJoin` prefab component is normally presented to the user before he enters a room.
 * This component allows the user to check and select the preferred media device (camera und microphone).
 * On submit the user decisions are returned, which can then be passed on to the `LiveKitRoom` so that the user enters the room with the correct media devices.
 *
 * @remarks
 * This component is independent of the `LiveKitRoom` component and should not be nested within it.
 * Because it only accesses the local media tracks this component is self-contained and works without connection to the LiveKit server.
 *
 * @example
 * ```tsx
 * <PreJoin />
 * ```
 * @public
 */
export function PreJoin({
  defaults = {},
  onValidate,
  onSubmit,
  onError,
  debug,
  joinLabel = 'Join Room',
  micLabel = 'Microphone',
  camLabel = 'Camera',
  userLabel = 'Username',
  roomName,
  persistUserChoices = true,
  videoProcessor,
  isHost = false,
  hq = false,
  ...htmlProps
}: PreJoinProps) {
  const videoQualityCtx = useOptionalVideoQuality();
  const previewProfile =
    videoQualityCtx?.videoQualityProfile ?? (hq ? 'max' : 'medium');

  /**
   * Nunca ler localStorage no useState initializer — no SSR o storage não existe e no
   * cliente o 1º paint divergiria (hydration mismatch: Mic vs MicOff, disabled, username).
   * Carregamos após mount e só então liberamos o save.
   */
  const [storageReady, setStorageReady] = React.useState(false);
  const {
    saveAudioInputDeviceId,
    saveAudioInputEnabled,
    saveVideoInputDeviceId,
    saveVideoInputEnabled,
    saveUsername,
  } = usePersistentUserChoices({
    defaults,
    preventSave: !persistUserChoices || !storageReady,
    preventLoad: true,
  });

  const [userChoices, setUserChoices] = React.useState<LocalUserChoices>(() =>
    loadUserChoices(defaults, true),
  );
  const [isLoading, setIsLoading] = React.useState(false);

  // Carregar configurações persistentes
  const { settings: persistentSettings, isLoaded: settingsLoaded } = useSettingsPersistence();

  // Defaults SSR-safe (iguais no server e no 1º paint do client)
  const [audioEnabled, setAudioEnabled] = React.useState<boolean>(defaults.audioEnabled ?? true);
  const [videoEnabled, setVideoEnabled] = React.useState<boolean>(defaults.videoEnabled ?? false);
  const [audioDeviceId, setAudioDeviceId] = React.useState<string>(
    defaults.audioDeviceId ?? 'default',
  );
  const [videoDeviceId, setVideoDeviceId] = React.useState<string>(
    defaults.videoDeviceId ?? 'default',
  );
  const [username, setUsername] = React.useState(defaults.username ?? '');
  const [displayRoomName, setDisplayRoomName] = React.useState(() => roomName ?? '');
  const [isHydrated, setIsHydrated] = React.useState(false);

  // activeSettingsTab removido - gerenciado pelo DeviceSettingsModal
  const [backgroundType, setBackgroundType] = React.useState<'none' | 'blur' | 'image'>('none');
  const [virtualBackgroundImagePath, setVirtualBackgroundImagePath] = React.useState<string>('');
  const [processorErrorCount, setProcessorErrorCount] = React.useState<number>(0);
  const {
    customBackgrounds,
    isUploading: isUploadingCustomBackground,
    error: customBackgroundError,
    uploadCustomBackground,
    removeCustomBackground,
    resetError: resetCustomBackgroundError,
    resolveCustomBackgroundPath,
    getCustomBackgroundIdByDataUrl,
  } = useCustomBackgroundImage();

  // Ref para o input de nome
  const usernameInputRef = React.useRef<HTMLInputElement>(null);

  // Aplicar focus no input de nome quando o componente for montado
  React.useEffect(() => {
    if (usernameInputRef.current) {
      usernameInputRef.current.focus();
      // Posicionar o cursor no final do texto
      const length = usernameInputRef.current.value.length;
      usernameInputRef.current.setSelectionRange(length, length);
    }
  }, []);

  React.useEffect(() => {
    if (roomName) {
      setDisplayRoomName(roomName);
    }
  }, [roomName]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedRoomName = sessionStorage.getItem('originalRoomName');
      if (storedRoomName) {
        setDisplayRoomName(storedRoomName);
      }
    } catch (error) {
      console.error('Failed to read originalRoomName from sessionStorage', error);
    }
  }, [roomName]);

  React.useEffect(() => {
    const hasCustomBackgrounds = customBackgrounds.length > 0;
    const customDataUrls = customBackgrounds.map((item) => item.dataUrl);

    if (!hasCustomBackgrounds) {
      if (
        backgroundType === 'image' &&
        virtualBackgroundImagePath &&
        virtualBackgroundImagePath.startsWith('data:')
      ) {
        setBackgroundType('none');
        setVirtualBackgroundImagePath('');
      }
      return;
    }

    if (backgroundType === 'image') {
      if (!virtualBackgroundImagePath) {
        const fallback = customBackgrounds[customBackgrounds.length - 1]?.dataUrl;
        if (fallback) {
          setVirtualBackgroundImagePath(fallback);
        }
        return;
      }

      if (
        virtualBackgroundImagePath.startsWith('data:') &&
        !customDataUrls.includes(virtualBackgroundImagePath)
      ) {
        const fallback = customBackgrounds[customBackgrounds.length - 1]?.dataUrl;
        if (fallback) {
          setVirtualBackgroundImagePath(fallback);
        } else {
          setBackgroundType('none');
          setVirtualBackgroundImagePath('');
        }
      }
    }
  }, [backgroundType, customBackgrounds, virtualBackgroundImagePath]);

  // Estados para dispositivos de áudio de saída
  const [audioOutputDeviceId, setAudioOutputDeviceId] = React.useState<string>('default');
  const [originalAudioOutputDeviceId, setOriginalAudioOutputDeviceId] =
    React.useState<string>('default');

  // Estados para indicador de áudio
  const [audioLevel, setAudioLevel] = React.useState<number>(0);
  const [isAnalyzingAudio, setIsAnalyzingAudio] = React.useState<boolean>(false);
  const [microphoneStatus, setMicrophoneStatus] = React.useState<
    'working' | 'not-working' | 'no-permission' | 'checking'
  >('checking');

  // Estados para som de teste
  const [isPlayingTestSound, setIsPlayingTestSound] = React.useState<boolean>(false);
  const [supportsSetSinkId, setSupportsSetSinkId] = React.useState<boolean>(false);

  // Estados originais mantidos para compatibilidade (DeviceSettingsModal gerencia suas próprias configurações)
  const [originalAudioEnabled, setOriginalAudioEnabled] = React.useState<boolean>(
    defaults.audioEnabled ?? true,
  );
  const [originalVideoEnabled, setOriginalVideoEnabled] = React.useState<boolean>(
    defaults.videoEnabled ?? false,
  );
  const [originalAudioDeviceId, setOriginalAudioDeviceId] = React.useState<string>(
    defaults.audioDeviceId ?? 'default',
  );
  const [originalVideoDeviceId, setOriginalVideoDeviceId] = React.useState<string>(
    defaults.videoDeviceId ?? 'default',
  );
  const [originalBackgroundType, setOriginalBackgroundType] = React.useState<
    'none' | 'blur' | 'image'
  >('none');
  const [originalVirtualBackgroundImagePath, setOriginalVirtualBackgroundImagePath] =
    React.useState<string>('');

  // Restaura preferências do localStorage só depois do mount (hydrated).
  React.useEffect(() => {
    const loaded = loadUserChoices(defaults, !persistUserChoices);
    setUserChoices(loaded);
    setAudioEnabled(loaded.audioEnabled);
    if (defaults.videoEnabled === undefined) {
      setVideoEnabled(loaded.videoEnabled);
    }
    setAudioDeviceId(loaded.audioDeviceId);
    setVideoDeviceId(loaded.videoDeviceId);
    setUsername(loaded.username);
    setOriginalAudioEnabled(loaded.audioEnabled);
    setOriginalVideoEnabled(
      defaults.videoEnabled !== undefined ? defaults.videoEnabled : loaded.videoEnabled,
    );
    setOriginalAudioDeviceId(loaded.audioDeviceId);
    setOriginalVideoDeviceId(loaded.videoDeviceId);
    setStorageReady(true);
    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore once on mount
  }, []);

  // Aplicar configurações persistentes quando carregadas
  React.useEffect(() => {
    if (settingsLoaded && persistentSettings) {
      // Aplicar configurações de dispositivos se estiverem definidas
      if (persistentSettings.audioDeviceId) {
        setAudioDeviceId(persistentSettings.audioDeviceId);
      }
      if (persistentSettings.videoDeviceId) {
        setVideoDeviceId(persistentSettings.videoDeviceId);
      }
      if (persistentSettings.audioOutputDeviceId) {
        setAudioOutputDeviceId(persistentSettings.audioOutputDeviceId);
      }
      if (persistentSettings.audioEnabled !== undefined) {
        setAudioEnabled(persistentSettings.audioEnabled);
      }
      // Só aplicar videoEnabled das configurações persistentes se não foi definido nos defaults
      if (persistentSettings.videoEnabled !== undefined && defaults.videoEnabled === undefined) {
        setVideoEnabled(persistentSettings.videoEnabled);
      }
      // Aplicar configurações de fundo virtual
      if (persistentSettings.backgroundType !== undefined) {
        setBackgroundType(persistentSettings.backgroundType);
      }
      if (persistentSettings.virtualBackgroundImagePath !== undefined) {
        const resolvedPath =
          resolveCustomBackgroundPath(persistentSettings.virtualBackgroundImagePath) ??
          persistentSettings.virtualBackgroundImagePath ??
          '';
        setVirtualBackgroundImagePath(resolvedPath);
      }
    }
  }, [settingsLoaded, persistentSettings]);

  // Tracks de pré-visualização utilizados pelo preview principal
  const audioTrack = usePreviewAudioTrack(audioEnabled, audioDeviceId, onError);

  const previewCaptureResolution = React.useMemo(
    () => getVideoQualityRoomCapture(previewProfile).videoCaptureDefaults.resolution!,
    [previewProfile],
  );
  const videoTrack = usePreviewVideoTrack(
    videoEnabled,
    videoDeviceId,
    onError,
    previewCaptureResolution,
  );

  const backgroundProcessorLockRef = React.useRef(false);
  const currentProcessorRef = React.useRef<'none' | 'blur' | 'image'>('none');
  const previousImagePathRef = React.useRef<string>('');

  // Track novo (câmera religada / troca de dispositivo) nasce sem processor: zera o
  // cache para o efeito abaixo reaplicar o fundo escolhido no preview.
  React.useEffect(() => {
    currentProcessorRef.current = 'none';
    previousImagePathRef.current = '';
  }, [videoTrack]);

  const applyBackgroundToPreview = React.useCallback(
    async (type: 'none' | 'blur' | 'image', imagePath?: string) => {
      if (!videoTrack) {
        return;
      }

      if (!videoEnabled) {
        try {
          await videoTrack.stopProcessor();
          currentProcessorRef.current = 'none';
        } catch {
          /* ignore */
        }
        return;
      }

      if (backgroundProcessorLockRef.current) {
        return;
      }

      if (
        currentProcessorRef.current === type &&
        (type !== 'image' || previousImagePathRef.current === imagePath)
      ) {
        return;
      }

      const delay = (ms: number): Promise<void> =>
        new Promise((resolve) => setTimeout(resolve, ms));
      backgroundProcessorLockRef.current = true;

      try {
        await delay(200);
        const targetType: 'none' | 'blur' | 'image' =
          type === 'image' && !imagePath ? 'none' : type;
        const result = await applyBackgroundToVideoTrack(videoTrack, {
          targetType,
          targetImage: targetType === 'image' ? imagePath : undefined,
          shouldApplyProcessor: targetType === 'blur' || targetType === 'image',
          delay,
        });
        currentProcessorRef.current = result.appliedType;
        previousImagePathRef.current =
          result.appliedType === 'image' ? imagePath ?? '' : '';
      } finally {
        backgroundProcessorLockRef.current = false;
      }
    },
    [videoTrack, videoEnabled],
  );

  const debouncedBackgroundType = useDebouncedValue(backgroundType, BACKGROUND_DEBOUNCE_MS);
  const debouncedVirtualBackgroundImagePath = useDebouncedValue(
    virtualBackgroundImagePath,
    BACKGROUND_DEBOUNCE_MS,
  );

  React.useEffect(() => {
    void applyBackgroundToPreview(debouncedBackgroundType, debouncedVirtualBackgroundImagePath);
  }, [
    applyBackgroundToPreview,
    debouncedBackgroundType,
    debouncedVirtualBackgroundImagePath,
  ]);

  React.useEffect(() => {
    const handleStorageChange = () => {
      try {
        const savedSettings = localStorage.getItem('evolutto-user-settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);

          // Atualizar configurações se mudaram (exceto durante cancelamento)
          // Evitar loops verificando se as configurações realmente mudaram
          if (
            parsedSettings.audioEnabled !== undefined &&
            parsedSettings.audioEnabled !== audioEnabled &&
            typeof parsedSettings.audioEnabled === 'boolean'
          ) {
            setAudioEnabled(parsedSettings.audioEnabled);
          }
          if (
            parsedSettings.videoEnabled !== undefined &&
            parsedSettings.videoEnabled !== videoEnabled &&
            typeof parsedSettings.videoEnabled === 'boolean'
          ) {
            setVideoEnabled(parsedSettings.videoEnabled);
          }
          if (
            parsedSettings.audioDeviceId !== undefined &&
            parsedSettings.audioDeviceId !== audioDeviceId
          ) {
            setAudioDeviceId(parsedSettings.audioDeviceId);
          }
          if (
            parsedSettings.videoDeviceId !== undefined &&
            parsedSettings.videoDeviceId !== videoDeviceId
          ) {
            setVideoDeviceId(parsedSettings.videoDeviceId);
          }
          if (
            parsedSettings.audioOutputDeviceId !== undefined &&
            parsedSettings.audioOutputDeviceId !== audioOutputDeviceId
          ) {
            setAudioOutputDeviceId(parsedSettings.audioOutputDeviceId);
          }
          if (
            parsedSettings.backgroundType !== undefined &&
            parsedSettings.backgroundType !== backgroundType
          ) {
            console.log('Prejoin: Sincronizando backgroundType', parsedSettings.backgroundType);
            setBackgroundType(parsedSettings.backgroundType);
          }
          if (parsedSettings.virtualBackgroundImagePath !== undefined) {
            const resolvedPath =
              resolveCustomBackgroundPath(parsedSettings.virtualBackgroundImagePath) ??
              parsedSettings.virtualBackgroundImagePath ??
              '';
            if (resolvedPath !== virtualBackgroundImagePath) {
              console.log(
                'Prejoin: Sincronizando virtualBackgroundImagePath',
                parsedSettings.virtualBackgroundImagePath,
              );
              setVirtualBackgroundImagePath(resolvedPath);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao sincronizar configurações:', error);
      }
    };

    // Escutar mudanças no localStorage
    window.addEventListener('storage', handleStorageChange);

    // Escutar evento customizado de configurações salvas
    const handleSettingsSaved = (event: CustomEvent) => {
      const savedSettings = event.detail;

      // Aplicar configurações imediatamente
      if (savedSettings.audioEnabled !== undefined && savedSettings.audioEnabled !== audioEnabled) {
        setAudioEnabled(savedSettings.audioEnabled);
      }
      if (savedSettings.videoEnabled !== undefined && savedSettings.videoEnabled !== videoEnabled) {
        setVideoEnabled(savedSettings.videoEnabled);
      }
      if (
        savedSettings.audioDeviceId !== undefined &&
        savedSettings.audioDeviceId !== audioDeviceId
      ) {
        setAudioDeviceId(savedSettings.audioDeviceId);
      }
      if (
        savedSettings.videoDeviceId !== undefined &&
        savedSettings.videoDeviceId !== videoDeviceId
      ) {
        setVideoDeviceId(savedSettings.videoDeviceId);
      }
      if (
        savedSettings.audioOutputDeviceId !== undefined &&
        savedSettings.audioOutputDeviceId !== audioOutputDeviceId
      ) {
        setAudioOutputDeviceId(savedSettings.audioOutputDeviceId);
      }
      if (
        savedSettings.backgroundType !== undefined &&
        savedSettings.backgroundType !== backgroundType
      ) {
        setBackgroundType(savedSettings.backgroundType);
      }
      if (savedSettings.virtualBackgroundImagePath !== undefined) {
        const resolvedPath =
          resolveCustomBackgroundPath(savedSettings.virtualBackgroundImagePath) ??
          savedSettings.virtualBackgroundImagePath ??
          '';
        if (resolvedPath !== virtualBackgroundImagePath) {
          setVirtualBackgroundImagePath(resolvedPath);
        }
        if (savedSettings.backgroundType) {
          void applyBackgroundToPreview(
            savedSettings.backgroundType,
            savedSettings.backgroundType === 'image' ? resolvedPath : undefined,
          );
        }
      } else if (savedSettings.backgroundType) {
        void applyBackgroundToPreview(savedSettings.backgroundType, undefined);
      }
    };

    window.addEventListener('settings-saved', handleSettingsSaved as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settings-saved', handleSettingsSaved as EventListener);
    };
  }, [
    audioEnabled,
    videoEnabled,
    audioDeviceId,
    videoDeviceId,
    audioOutputDeviceId,
    backgroundType,
    virtualBackgroundImagePath,
    applyBackgroundToPreview,
  ]);

  // Função para selecionar fundo virtual
  const selectBackground = (type: 'none' | 'blur' | 'image', imagePath?: string) => {
    setBackgroundType(type);
    if (type === 'image' && imagePath) {
      setVirtualBackgroundImagePath(imagePath);
    } else if (type !== 'image') {
      setVirtualBackgroundImagePath((current) =>
        current && current.startsWith('data:') ? '' : current,
      );
    }
  };

  const handleUploadCustomBackground = async (file: File) => {
    const result = await uploadCustomBackground(file);
    if (result.success && result.dataUrl) {
      selectBackground('image', result.dataUrl);
    }
    return result;
  };

  const handleRemoveCustomBackground = (id: string) => {
    const remaining = customBackgrounds.filter((item) => item.id !== id);
    removeCustomBackground(id);
    if (backgroundType === 'image' && virtualBackgroundImagePath) {
      const stillExists = remaining.some((item) => item.dataUrl === virtualBackgroundImagePath);
      if (!stillExists) {
        if (remaining.length > 0) {
          setVirtualBackgroundImagePath(remaining[remaining.length - 1].dataUrl);
        } else {
          setBackgroundType('none');
          setVirtualBackgroundImagePath('');
        }
      }
    }
  };

  // Função para tocar som de teste
  const playTestSound = async () => {
    if (isPlayingTestSound) return;

    try {
      setIsPlayingTestSound(true);

      // Usar elemento de áudio para ter controle sobre o dispositivo de saída
      const audio = new Audio();

      // Configurar o som de teste (440Hz - Lá musical)
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();

      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

      oscillator.connect(gainNode);
      gainNode.connect(destination);

      // Criar stream de áudio
      const stream = destination.stream;
      audio.srcObject = stream;
      audio.volume = 0.3;

      // Configurar o dispositivo de saída específico
      if (audioOutputDeviceId !== 'default' && 'setSinkId' in audio) {
        try {
          await (audio as any).setSinkId(audioOutputDeviceId);
        } catch (error) {
          console.warn('Não foi possível configurar dispositivo específico, usando padrão:', error);
        }
      }

      // Tocar o som
      oscillator.start(audioContext.currentTime);
      await audio.play();

      // Fade out suave
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);

      // Parar após 2 segundos
      setTimeout(() => {
        oscillator.stop();
        audio.pause();
        audioContext.close();
        setIsPlayingTestSound(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao reproduzir som de teste:', error);
      setIsPlayingTestSound(false);

      // Fallback: usar um som simples com setSinkId
      try {
        const audio = new Audio();
        audio.src =
          'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
        audio.volume = 0.3;

        // Tentar configurar dispositivo de saída no fallback também
        if (audioOutputDeviceId !== 'default' && 'setSinkId' in audio) {
          try {
            await (audio as any).setSinkId(audioOutputDeviceId);
          } catch (sinkError) {
            console.warn('Erro ao configurar dispositivo no fallback:', sinkError);
          }
        }

        await audio.play();

        setTimeout(() => {
          setIsPlayingTestSound(false);
        }, 2000);
      } catch (fallbackError) {
        console.error('Erro no fallback de áudio:', fallbackError);
        setIsPlayingTestSound(false);
      }
    }
  };

  // Hook para análise de áudio em tempo real
  const useAudioLevelAnalyzer = (audioTrack: LocalAudioTrack | undefined) => {
    const analyserRef = React.useRef<AnalyserNode | null>(null);
    const animationFrameRef = React.useRef<number | null>(null);
    const audioContextRef = React.useRef<AudioContext | null>(null);
    const audioDetectionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const hasDetectedAudioRef = React.useRef<boolean>(false);

    React.useEffect(() => {
      // Reset state when audio track changes
      hasDetectedAudioRef.current = false;
      setMicrophoneStatus('checking');

      if (!audioTrack || !audioEnabled) {
        setIsAnalyzingAudio(false);
        setAudioLevel(0);
        setMicrophoneStatus('not-working');
        return;
      }

      const startAudioAnalysis = async () => {
        try {
          // Verificar permissões de microfone
          const stream = audioTrack.mediaStream;
          if (!stream) {
            setMicrophoneStatus('no-permission');
            return;
          }

          // Verificar se o stream tem áudio ativo
          const audioTracks = stream.getAudioTracks();
          if (audioTracks.length === 0 || audioTracks[0].readyState !== 'live') {
            setMicrophoneStatus('no-permission');
            return;
          }

          // Se chegou até aqui, o microfone está disponível e funcionando
          // Vamos dar uma chance para detectar áudio, mas se não detectar em 1 segundo,
          // vamos marcar como funcionando mesmo assim (pode ser que esteja silencioso)
          setTimeout(() => {
            if (!hasDetectedAudioRef.current) {
              hasDetectedAudioRef.current = true;
              setMicrophoneStatus('working');
            }
          }, 1000);

          // Criar contexto de áudio
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioContext;

          // Criar analisador
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.8;
          analyserRef.current = analyser;

          // Conectar o stream de áudio ao analisador
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);

          setIsAnalyzingAudio(true);

          // Função para analisar o nível de áudio
          const analyzeAudio = () => {
            if (!analyserRef.current) return;

            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteFrequencyData(dataArray);

            // Calcular o nível médio de áudio
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
              sum += dataArray[i];
            }
            const average = sum / dataArray.length;
            const normalizedLevel = Math.min(average / 128, 1); // Normalizar para 0-1

            setAudioLevel(normalizedLevel);

            // Detectar se o microfone está funcionando
            // Usar threshold ainda mais baixo para detectar qualquer atividade
            const isWorking = normalizedLevel > 0.001; // Threshold extremamente baixo

            if (isWorking && !hasDetectedAudioRef.current) {
              hasDetectedAudioRef.current = true;
              setMicrophoneStatus('working');
            } else if (!isWorking && !hasDetectedAudioRef.current) {
              // Se ainda não detectou áudio, aguardar um pouco mais
              // O timeout será definido apenas uma vez
              if (!audioDetectionTimeoutRef.current) {
                audioDetectionTimeoutRef.current = setTimeout(() => {
                  if (!hasDetectedAudioRef.current) {
                    setMicrophoneStatus('not-working');
                  }
                }, 2000); // Reduzir para 2 segundos
              }
            }

            // Continuar a análise
            animationFrameRef.current = requestAnimationFrame(analyzeAudio);
          };

          analyzeAudio();
        } catch (error) {
          console.error('Erro ao iniciar análise de áudio:', error);
          setIsAnalyzingAudio(false);
          setMicrophoneStatus('no-permission');
        }
      };

      startAudioAnalysis();

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (audioDetectionTimeoutRef.current) {
          clearTimeout(audioDetectionTimeoutRef.current);
        }
        setIsAnalyzingAudio(false);
        setAudioLevel(0);
        hasDetectedAudioRef.current = false;
      };
    }, [audioTrack, audioEnabled]); // Remover microphoneStatus da dependência para evitar loop

    return { audioLevel, isAnalyzingAudio };
  };

  // Função para salvar o estado atual antes de abrir o modal
  const saveCurrentSettings = () => {
    setOriginalAudioEnabled(audioEnabled);
    setOriginalVideoEnabled(videoEnabled);
    setOriginalAudioDeviceId(audioDeviceId);
    setOriginalVideoDeviceId(videoDeviceId);
    setOriginalBackgroundType(backgroundType);
    setOriginalVirtualBackgroundImagePath(virtualBackgroundImagePath);
    setOriginalAudioOutputDeviceId(audioOutputDeviceId);
  };

  // Função para abrir modal com aba específica
  const openSettingsWithTab = (tab: 'audio' | 'video' | 'backgrounds') => {
    saveCurrentSettings(); // Salvar configurações atuais antes de abrir
    setSettingsTab(tab);
    setSettingsOpen(true);
  };

  // Função para abrir modal mobile com aba específica
  const openMobileSettingsWithTab = (tab: 'audio' | 'video' | 'backgrounds') => {
    setMobileSettingsView(tab);
    setMobileSettingsOpen(true);
  };

  // Função para restaurar as configurações originais (apenas estados locais do Prejoin)
  const restoreOriginalSettings = () => {
    setAudioEnabled(originalAudioEnabled);
    setVideoEnabled(originalVideoEnabled);
    setAudioDeviceId(originalAudioDeviceId);
    setVideoDeviceId(originalVideoDeviceId);
    setBackgroundType(originalBackgroundType);
    setVirtualBackgroundImagePath(originalVirtualBackgroundImagePath);
    setAudioOutputDeviceId(originalAudioOutputDeviceId);
  };

  // Save user choices to persistent storage (só após restore do localStorage).
  React.useEffect(() => {
    if (!storageReady || !settingsLoaded) return;
    saveAudioInputEnabled(audioEnabled);
  }, [audioEnabled, saveAudioInputEnabled, settingsLoaded, storageReady]);
  React.useEffect(() => {
    if (!storageReady || !settingsLoaded) return;
    saveVideoInputEnabled(videoEnabled);
  }, [videoEnabled, saveVideoInputEnabled, settingsLoaded, storageReady]);
  React.useEffect(() => {
    if (!storageReady) return;
    saveAudioInputDeviceId(audioDeviceId);
  }, [audioDeviceId, saveAudioInputDeviceId, storageReady]);
  React.useEffect(() => {
    if (!storageReady) return;
    saveVideoInputDeviceId(videoDeviceId);
  }, [videoDeviceId, saveVideoInputDeviceId, storageReady]);
  React.useEffect(() => {
    if (!storageReady) return;
    saveUsername(username);
  }, [username, saveUsername, storageReady]);

  const audioInputDevices = useMediaDevices({ kind: 'audioinput' });
  const videoInputDevices = useMediaDevices({ kind: 'videoinput' });
  const audioOutputDevices = useMediaDevices({ kind: 'audiooutput' });

  // Ref e largura para centralizar/alargar o dropdown da seta conforme o botão completo
  const joinContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [joinMenuWidth, setJoinMenuWidth] = React.useState<number | null>(null);

  React.useEffect(() => {
    const updateWidth = () => {
      const width = joinContainerRef.current?.offsetWidth;
      if (width && width !== joinMenuWidth) {
        setJoinMenuWidth(width);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [joinMenuWidth]);
  const facingMode = React.useMemo(() => {
    if (videoTrack) {
      const { facingMode } = facingModeFromLocalTrack(videoTrack);
      return facingMode;
    } else {
      return 'undefined';
    }
  }, [videoTrack]);

  // Usar o hook de análise de áudio
  const { audioLevel: realTimeAudioLevel, isAnalyzingAudio: isAnalyzing } =
    useAudioLevelAnalyzer(audioTrack);

  // Verificar suporte ao setSinkId e permissões de microfone
  React.useEffect(() => {
    const audio = new Audio();
    setSupportsSetSinkId('setSinkId' in audio);

    // Verificar permissões de microfone
    const checkMicrophonePermissions = async () => {
      try {
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });
          if (permission.state === 'denied') {
            setMicrophoneStatus('no-permission');
          }
        }
      } catch (error) {
        // Ignorar erros de verificação de permissões
      }
    };

    checkMicrophonePermissions();
  }, []);

  // Efeito para detectar quando o áudio track muda e resetar o status
  React.useEffect(() => {
    if (audioTrack && audioEnabled) {
      setMicrophoneStatus('checking');
    } else if (!audioEnabled) {
      setMicrophoneStatus('not-working');
    }
  }, [audioTrack, audioEnabled]);

  React.useEffect(() => {
    if (!videoTrack) {
      return;
    }
    if (videoEnabled && videoTrack.isMuted) {
      videoTrack.unmute();
    } else if (!videoEnabled && !videoTrack.isMuted) {
      videoTrack.mute();
    }
  }, [videoTrack, videoEnabled]);

  const [isValid, setIsValid] = React.useState<boolean>();

  const handleValidation = React.useCallback(
    (values: LocalUserChoices) => {
      if (typeof onValidate === 'function') {
        return onValidate(values);
      } else {
        return values.username !== '';
      }
    },
    [onValidate],
  );

  React.useEffect(() => {
    const newUserChoices = {
      username,
      videoEnabled,
      videoDeviceId,
      audioEnabled,
      audioDeviceId,
      backgroundType,
      virtualBackgroundImagePath,
    };
    setUserChoices(newUserChoices);
    setIsValid(handleValidation(newUserChoices));
  }, [username, videoEnabled, handleValidation, audioEnabled, audioDeviceId, videoDeviceId]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (handleValidation(userChoices)) {
      if (typeof onSubmit === 'function') {
        const extendedUserChoices = {
          ...userChoices,
          backgroundType,
          virtualBackgroundImagePath,
        };
        void unlockBrowserAudioPlayback();
        onSubmit(extendedUserChoices);
      }
    } else {
      log.warn('Validation failed with: ', userChoices);
    }
  }

  function handleJoinWithoutAudio(event: React.FormEvent) {
    event.preventDefault();
    const userChoicesWithoutAudio = {
      ...userChoices,
      audioEnabled: false,
      backgroundType,
      virtualBackgroundImagePath,
    };
    if (handleValidation(userChoicesWithoutAudio)) {
      if (typeof onSubmit === 'function') {
        void unlockBrowserAudioPlayback();
        onSubmit(userChoicesWithoutAudio);
      }
    } else {
      log.warn('Validation failed with: ', userChoicesWithoutAudio);
    }
  }

  // Handle Enter key press in input
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (handleValidation(userChoices)) {
        if (typeof onSubmit === 'function') {
          void unlockBrowserAudioPlayback();
          onSubmit(userChoices);
        }
      } else {
        log.warn('Validation failed with: ', userChoices);
      }
    }
  };

  // Estados e funções do modal de convite
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteUrl, setInviteUrl] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  // Novo estado para o modal de configurações
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsTab, setSettingsTab] = React.useState<'audio' | 'video' | 'backgrounds'>('audio');

  // Estados para modal mobile em tela cheia
  const [mobileSettingsOpen, setMobileSettingsOpen] = React.useState(false);
  const [mobileSettingsView, setMobileSettingsView] = React.useState<
    'menu' | 'audio' | 'video' | 'backgrounds'
  >('menu');

  // Estado para modal de convite mobile
  const [mobileInviteOpen, setMobileInviteOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      const path = roomName ? `/${roomName}` : window.location.pathname;
      // Se for anfitrião, mostrar link de convidado (sem parâmetro host.
      const guestPath = path.replace('?host=true', '');
      setInviteUrl(`${origin}${guestPath}`);
    }
  }, [roomName]);

  const handleCopyLink = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  }, [inviteUrl]);

  useWarnAboutMissingStyles();

  // SSR-safe: isWeakClientDevice() usa navigator — só avaliar após mount
  const [showWeakDeviceHint, setShowWeakDeviceHint] = React.useState(false);
  React.useEffect(() => {
    setShowWeakDeviceHint(isWeakClientDevice());
  }, []);

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#0b0b14]">

      {/* ── DESKTOP LAYOUT ── */}
      <div className="hidden lg:flex h-full">

        {/* ── COLUNA ESQUERDA ── */}
        <div className="w-[380px] min-w-[380px] flex flex-col bg-[#0e0e1c] border-r border-white/[0.06] px-8 py-10 overflow-y-auto">

          <p className="text-[10px] font-semibold text-[#3d75f0] tracking-[0.1em] uppercase mb-2">
            Evolu Meet
          </p>

          <h1 className="text-2xl font-bold text-[#f0f0fa] leading-tight tracking-tight mb-5">
            {isHost ? 'Iniciar como anfitrião' : 'Entrar na reunião'}
          </h1>

          {/* Badge sala */}
          {roomName && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#181830] border border-white/[0.07] text-xs font-medium text-[#606080] w-fit mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              {displayRoomName || roomName}
            </div>
          )}

          {/* Badge Anfitrião */}
          {isHost && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/[0.08] border border-amber-500/[0.18] text-xs font-semibold text-amber-400 w-fit mb-7">
              <span>👑</span> Anfitrião
            </div>
          )}

          {showWeakDeviceHint && (
            <div
              className="mb-5 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed text-amber-100/90"
              role="status"
            >
              Seu dispositivo tem recursos limitados. Para uma reunião mais fluida, evite fundo
              virtual e prefira desligar a câmera quando não estiver falando.
            </div>
          )}

          {/* Input nome */}
          <div className="mb-4">
            <label className="block text-[10px] font-semibold text-[#404060] tracking-[0.08em] uppercase mb-2">
              Seu nome
            </label>
            <input
              ref={usernameInputRef}
              type="text"
              placeholder="Insira seu nome"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3.5 py-3 rounded-xl bg-[#181830] border border-white/[0.09] text-[#e8e8f4] text-sm font-medium outline-none placeholder:text-[#35354e] focus:border-[#3d75f0]/50 transition-colors"
            />
          </div>

          {/* CTA principal */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={username.length === 0 || isLoading}
            className="w-full py-3.5 px-5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 mb-2 bg-gradient-to-br from-[#3d75f0] to-[#2255cc] shadow-[0_4px_20px_rgba(61,117,240,0.3),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_6px_28px_rgba(61,117,240,0.45)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isHost ? 'Iniciar como anfitrião' : 'Entrar na reunião'}
          </button>

          {/* Opção sem áudio */}
          <button
            type="button"
            onClick={handleJoinWithoutAudio}
            disabled={username.length === 0 || isLoading}
            className="w-full py-2 text-xs text-[#505070] hover:text-[#7070a0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed mb-6"
          >
            Entrar sem áudio
          </button>

          <div className="h-px bg-white/[0.06] mb-6" />
          <p className="text-[10px] font-semibold text-[#303050] tracking-[0.08em] uppercase mb-3.5">
            Configurar dispositivos
          </p>

          {/* Botões de dispositivo */}
          <div className="flex items-center gap-2">
            {/* Mic */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center border cursor-pointer transition-colors ${
                      audioEnabled
                        ? 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]'
                        : 'bg-red-500/10 border-red-500/20'
                    }`}
                    onClick={() => setAudioEnabled((v) => !v)}
                  >
                    {audioEnabled ? (
                      <Mic className="w-[18px] h-[18px] text-white/60" />
                    ) : (
                      <MicOff className="w-[18px] h-[18px] text-red-400" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>{audioEnabled ? 'Desativar microfone' : 'Ativar microfone'}</p></TooltipContent>
              </Tooltip>

              {/* Camera */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center border cursor-pointer transition-colors ${
                      videoEnabled
                        ? 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]'
                        : 'bg-red-500/10 border-red-500/20'
                    }`}
                    onClick={() => setVideoEnabled((v) => !v)}
                  >
                    {videoEnabled ? (
                      <Video className="w-[18px] h-[18px] text-white/60" />
                    ) : (
                      <VideoOff className="w-[18px] h-[18px] text-red-400" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>{videoEnabled ? 'Desativar câmera' : 'Ativar câmera'}</p></TooltipContent>
              </Tooltip>

              {/* Convidar */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer hover:bg-white/[0.08] transition-colors"
                    onClick={() => setInviteOpen(true)}
                  >
                    <UserPlus className="w-[18px] h-[18px] text-white/50" />
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>Convidar pessoas</p></TooltipContent>
              </Tooltip>

              {/* Fundo virtual */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer hover:bg-white/[0.08] transition-colors"
                    onClick={() => openSettingsWithTab('backgrounds')}
                  >
                    <ImageIcon className="w-[18px] h-[18px] text-white/50" />
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>Fundo virtual</p></TooltipContent>
              </Tooltip>

              {/* Configurações */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer hover:bg-white/[0.08] transition-colors"
                    onClick={() => openSettingsWithTab('audio')}
                  >
                    <Settings className="w-[18px] h-[18px] text-white/50" />
                  </div>
                </TooltipTrigger>
                <TooltipContent><p>Configurações</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Aviso microfone desativado */}
          {!audioEnabled && (
            <div className="mt-5 flex items-start gap-2 p-2.5 rounded-[10px] bg-red-500/[0.07] border border-red-500/[0.14]">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
              <span className="text-xs text-red-400 leading-relaxed">
                Microfone desativado — ative para participar com áudio
              </span>
            </div>
          )}

          {/* Status do microfone */}
          <div className="flex items-center gap-2 mt-4 text-xs">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              microphoneStatus === 'working' ? 'bg-green-500' :
              microphoneStatus === 'checking' ? 'bg-yellow-500 animate-pulse' :
              microphoneStatus === 'no-permission' ? 'bg-red-500' : 'bg-gray-500'
            }`} />
            <span className="text-[#606080]">
              {microphoneStatus === 'working' && 'Microfone funcionando'}
              {microphoneStatus === 'checking' && 'Verificando microfone...'}
              {microphoneStatus === 'no-permission' && 'Permita o acesso ao microfone'}
              {microphoneStatus === 'not-working' && 'Microfone não encontrado'}
            </span>
          </div>
        </div>

        {/* ── COLUNA DIREITA: preview de câmera ── */}
        <div className="flex-1 bg-[#0b0b14] flex items-center justify-center p-10">
          <div className="w-full max-w-[620px] aspect-video rounded-3xl overflow-hidden relative bg-[#0f0f1e] border border-white/[0.08] shadow-[0_40px_90px_rgba(0,0,0,0.65)]">

            {/* Câmera off */}
            {!videoEnabled && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[radial-gradient(ellipse_at_50%_40%,#1c1c30,#0b0b18)]">
                <div className="w-20 h-20 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                  <VideoOff className="w-8 h-8 text-white/20" />
                </div>
                <p className="text-white/30 text-base font-medium">Câmera desativada</p>
                <p className="text-white/15 text-sm">Ative a câmera para ver sua imagem</p>
              </div>
            )}

            {/* Vídeo */}
            <PrejoinVideoPreview
              videoTrack={videoTrack}
              videoEnabled={videoEnabled}
              facingMode={facingMode}
              mirror={facingMode === 'user' && debouncedBackgroundType === 'none'}
            />

            {/* Overlay inferior */}
            <div className="absolute bottom-0 left-0 right-0 px-5 pt-9 pb-4 bg-gradient-to-t from-black/75 to-transparent flex items-end justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[15px] font-semibold text-white/90">{username || 'Você'}</span>
                {isHost && (
                  <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/[0.22] px-2 py-0.5 rounded-lg">
                    Anfitrião
                  </span>
                )}
              </div>
              <div className="flex gap-1.5">
                <div
                  className="w-[34px] h-[34px] rounded-[9px] bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => setAudioEnabled((v) => !v)}
                >
                  {audioEnabled
                    ? <Mic className="w-4 h-4 text-white/80" />
                    : <MicOff className="w-4 h-4 text-red-400" />}
                </div>
                <div
                  className="w-[34px] h-[34px] rounded-[9px] bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => setVideoEnabled((v) => !v)}
                >
                  {videoEnabled
                    ? <Video className="w-4 h-4 text-white/80" />
                    : <VideoOff className="w-4 h-4 text-red-400" />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── LAYOUT MOBILE ── */}
      <div className="lg:hidden h-full flex flex-col">
        {/* Header Mobile */}
        <div className="bg-[#0e0e1c] p-4 flex items-center justify-between border-b border-white/[0.06]">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="bg-transparent border-white/[0.12] text-white/70 hover:bg-white/[0.08]"
          >
            <Link href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Link>
          </Button>
          {isHost && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/[0.08] border border-amber-500/[0.18] text-xs font-semibold text-amber-400">
              <span>👑</span> Anfitrião
            </div>
          )}
        </div>

        {showWeakDeviceHint && (
          <div
            className="mx-4 mt-3 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-100/90"
            role="status"
          >
            Dispositivo com recursos limitados — evite fundo virtual para melhor fluidez.
          </div>
        )}

        {/* Video Preview Mobile */}
        <div className="flex-1 bg-[#0b0b14] relative">
          <PrejoinVideoPreview
            videoTrack={videoTrack}
            videoEnabled={videoEnabled}
            facingMode={facingMode}
            mirror={facingMode === 'user' && debouncedBackgroundType === 'none'}
          />
          {!videoEnabled && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[radial-gradient(ellipse_at_50%_40%,#1c1c30,#0b0b18)]">
              <div className="w-16 h-16 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                <VideoOff className="w-7 h-7 text-white/20" />
              </div>
              <p className="text-white/30 text-sm font-medium">Câmera desativada</p>
            </div>
          )}
        </div>

        {/* Controls Mobile */}
        <div className="bg-[#0e0e1c] p-5 space-y-4 border-t border-white/[0.06]">
          <div className="text-center">
            <h1 className="text-lg font-bold text-[#f0f0fa]">
              {isHost ? 'Iniciar como anfitrião' : 'Entrar na reunião'}
            </h1>
            {roomName && (
              <p className="text-[#505070] text-sm mt-1">{displayRoomName || roomName}</p>
            )}
          </div>

          <input
            ref={usernameInputRef}
            type="text"
            placeholder="Insira seu nome"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full px-3.5 py-3 rounded-xl bg-[#181830] border border-white/[0.09] text-[#e8e8f4] text-sm font-medium outline-none placeholder:text-[#35354e] focus:border-[#3d75f0]/50 transition-colors"
          />

          <button
            type="button"
            onClick={handleSubmit}
            disabled={username.length === 0 || isLoading}
            className="w-full py-3.5 px-5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 bg-gradient-to-br from-[#3d75f0] to-[#2255cc] shadow-[0_4px_20px_rgba(61,117,240,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isHost ? 'Iniciar como anfitrião' : 'Entrar na reunião'}
          </button>

          <div className="flex items-center justify-center gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center border cursor-pointer transition-colors ${audioEnabled ? 'bg-white/[0.04] border-white/[0.08]' : 'bg-red-500/10 border-red-500/20'}`}
              onClick={() => setAudioEnabled((v) => !v)}
            >
              {audioEnabled ? <Mic className="w-5 h-5 text-white/60" /> : <MicOff className="w-5 h-5 text-red-400" />}
            </div>
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center border cursor-pointer transition-colors ${videoEnabled ? 'bg-white/[0.04] border-white/[0.08]' : 'bg-red-500/10 border-red-500/20'}`}
              onClick={() => setVideoEnabled((v) => !v)}
            >
              {videoEnabled ? <Video className="w-5 h-5 text-white/60" /> : <VideoOff className="w-5 h-5 text-red-400" />}
            </div>
            <div
              className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer"
              onClick={() => setMobileInviteOpen(true)}
            >
              <UserPlus className="w-5 h-5 text-white/50" />
            </div>
            <div
              className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer"
              onClick={() => openMobileSettingsWithTab('backgrounds')}
            >
              <ImageIcon className="w-5 h-5 text-white/50" />
            </div>
            <div
              className="w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer"
              onClick={() => { setMobileSettingsView('menu'); setMobileSettingsOpen(true); }}
            >
              <Settings className="w-5 h-5 text-white/50" />
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-xs">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              microphoneStatus === 'working' ? 'bg-green-500' :
              microphoneStatus === 'checking' ? 'bg-yellow-500 animate-pulse' :
              microphoneStatus === 'no-permission' ? 'bg-red-500' : 'bg-gray-500'
            }`} />
            <span className="text-[#505070] text-center">
              {microphoneStatus === 'working' && 'Microfone funcionando'}
              {microphoneStatus === 'checking' && 'Verificando...'}
              {microphoneStatus === 'no-permission' && 'Permita acesso ao microfone'}
              {microphoneStatus === 'not-working' && 'Microfone não funciona'}
            </span>
          </div>
        </div>
      </div>

      {/* ── MODALS (mantidos integralmente) ── */}

      {/* Dialog de convite desktop */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md bg-[#0e0e1c] border-white/[0.08] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#f0f0fa]">Convidar pessoas</DialogTitle>
            <DialogDescription className="text-[#6868a0]">
              {isHost ? 'Compartilhe o link com os convidados' : 'Compartilhe o link da reunião'}
            </DialogDescription>
          </DialogHeader>
          {isHost && (
            <div className="bg-amber-500/[0.08] border border-amber-500/[0.18] rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/[0.22] px-2 py-0.5 rounded-lg">
                  👑 Anfitrião
                </span>
              </div>
              <p className="text-amber-200/70 text-sm">
                Você está entrando como anfitrião. Compartilhe o link abaixo com os convidados.
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={inviteUrl}
              className="flex-1 bg-[#181830] border-white/[0.09] text-[#e8e8f4]"
            />
            <Button
              onClick={handleCopyLink}
              className="shrink-0 bg-[#3d75f0] hover:bg-[#5590ff] text-white"
            >
              <Copy className="w-4 h-4 mr-1" />
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <div className="mt-4">
            <p className="text-sm text-[#6868a0] mb-3">Compartilhar por:</p>
            <div className="flex items-center gap-3">
              <a href={`mailto:?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="icon" title="E-mail" className="border-white/[0.09] text-white/70 hover:bg-white/[0.08]">
                  <Mail className="w-4 h-4" />
                </Button>
              </a>
              <a href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="icon" title="Gmail" className="border-white/[0.09] text-white/70 hover:bg-white/[0.08]">G</Button>
              </a>
              <a href={`https://outlook.live.com/mail/deeplink/compose?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="icon" title="Outlook" className="border-white/[0.09] text-white/70 hover:bg-white/[0.08]">O</Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DeviceSettingsModal — controlado via estado (trigger interno invisível) */}
      <DeviceSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        defaultTab={settingsTab}
        onCancel={restoreOriginalSettings}
        trigger={<span className="hidden" />}
      />

      {/* MobileSettingsModal */}
      <MobileSettingsModal
        open={mobileSettingsOpen}
        onOpenChange={setMobileSettingsOpen}
        view={mobileSettingsView}
        onViewChange={setMobileSettingsView}
        audioEnabled={audioEnabled}
        setAudioEnabled={setAudioEnabled}
        videoEnabled={videoEnabled}
        setVideoEnabled={setVideoEnabled}
        audioDeviceId={audioDeviceId}
        setAudioDeviceId={setAudioDeviceId}
        videoDeviceId={videoDeviceId}
        setVideoDeviceId={setVideoDeviceId}
        audioInputDevices={audioInputDevices}
        videoInputDevices={videoInputDevices}
        audioOutputDevices={audioOutputDevices}
        audioOutputDeviceId={audioOutputDeviceId}
        setAudioOutputDeviceId={setAudioOutputDeviceId}
        videoTrack={videoTrack}
        backgroundType={backgroundType}
        virtualBackgroundImagePath={virtualBackgroundImagePath}
        selectBackground={selectBackground}
        customBackgrounds={customBackgrounds}
        uploadCustomBackground={handleUploadCustomBackground}
        removeCustomBackground={handleRemoveCustomBackground}
        isUploadingCustomBackground={isUploadingCustomBackground}
        customBackgroundError={customBackgroundError}
        resetCustomBackgroundError={resetCustomBackgroundError}
        audioLevel={realTimeAudioLevel}
        isAnalyzingAudio={isAnalyzing}
        isPlayingTestSound={isPlayingTestSound}
        playTestSound={playTestSound}
        supportsSetSinkId={supportsSetSinkId}
      />

      {/* MobileInviteModal */}
      <MobileInviteModal
        open={mobileInviteOpen}
        onOpenChange={setMobileInviteOpen}
        inviteUrl={inviteUrl}
        onCopyLink={handleCopyLink}
        copied={copied}
        isHost={isHost}
      />
    </div>
  );
}
